import PropTypes from "prop-types";
import React, {useCallback, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";

import Button from "layout/atoms/Button";
import Dropdown from "layout/atoms/Dropdown";
import Icon from "layout/atoms/Icon";
import Status from "layout/molecules/Status";
import VotesCounter from "layout/molecules/VotesCounter";

import EditMode from "./EditMode";
import Options from "./Options";

import {ReactComponent as EditSvg} from "assets/icons/edit1.svg";
import {ReactComponent as MessageSvg} from "assets/icons/message-square.svg";
import {ReactComponent as TrashSvg} from "assets/icons/trash.svg";
import {changeIdeaStatus, deleteIdea, downvoteIdea, selectIdeaById, upvoteIdea,} from "store/currentBoardSlice";
import {openModal} from "store/modalsSlice";
import {Main, Wrapper} from "./styles";

const IdeaCard = ({ ideaId, boardId, viewMode = "normal" }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("translation", {
    keyPrefix: "boardsDetails.ideaCard",
  });
  const idea = useSelector((state) => selectIdeaById(state, ideaId));
  const [editMode, setEditMode] = useState(false);

  const handleUpvoteClick = useCallback(() => {
    dispatch(upvoteIdea({ ideaId, boardId }));
  }, [dispatch, ideaId, boardId]);

  const handleDownvoteClick = useCallback(() => {
    dispatch(downvoteIdea({ ideaId, boardId }));
  }, [dispatch, ideaId, boardId]);

  const handleTitleClick = useCallback(() => {
    dispatch(openModal({ name: "ideaModal", data: { ideaId, boardId } }));
  }, [dispatch, ideaId, boardId]);

  const handleStatusChange = useCallback(
    (e) => {
      dispatch(changeIdeaStatus({ status: e, ideaId, boardId }));
    },
    [dispatch, ideaId, boardId],
  );

  const handleDelete = useCallback(() => {
    dispatch(deleteIdea({ ideaId, boardId }));
  }, [dispatch, ideaId, boardId]);

  const handleDeclineClick = useCallback(() => {
    dispatch(
      openModal({ name: "declineIdeaModal", data: { ideaId, boardId } }),
    );
  }, [dispatch, ideaId, boardId]);

  if (!idea) return null;

  return (
    <Wrapper $viewMode={viewMode}>
      <VotesCounter
        votesNumber={idea.voteCount}
        isVotedByMe={idea.isVotedByMe}
        onVoteClick={idea.isVotedByMe ? handleDownvoteClick : handleUpvoteClick}
        small={viewMode === "compact"}
      />
      <Main $viewMode={viewMode}>
        {idea.title && !editMode && (
          <h4 onClick={handleTitleClick}>{idea.title}</h4>
        )}
        {idea.canEdit && editMode && (
          <EditMode
            ideaId={ideaId}
            boardId={boardId}
            title={idea.title}
            description={idea.description}
            status={idea.status}
            canUpdateStatus={idea.canUpdateStatus}
            onCancel={() => setEditMode(false)}
            onSave={() => setEditMode(false)}
          />
        )}
      </Main>
      <Options viewMode={viewMode}>
        <Status
          statusName={idea.status}
          canUpdateStatus={idea.canUpdateStatus}
          onStatusClick={handleStatusChange}
          onDeclineClick={handleDeclineClick}
        />
        <Button
          size="sm-square"
          color="white"
          onClick={handleTitleClick}
          disabled={idea.commentCount === 0}
        >
          <Icon src={MessageSvg} />
          <p>{idea.commentCount}</p>
        </Button>
        {idea.canEdit && (
          <Dropdown closeOnClick>
            <Button
              size="md"
              color="white"
              disabled={editMode}
              onClick={() => setEditMode(true)}
            >
              <Icon src={EditSvg} />
              <p>{t("edit")}</p>
            </Button>
            <Button size="md" color="white" onClick={handleDelete}>
              <Icon src={TrashSvg} />
              <p>{t("remove")}</p>
            </Button>
          </Dropdown>
        )}
      </Options>
    </Wrapper>
  );
};

IdeaCard.propTypes = {
  ideaId: PropTypes.string,
  boardId: PropTypes.string,
  viewMode: PropTypes.oneOf(["normal", "compact"]),
};

export default React.memo(IdeaCard);
